import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Badge from "../components/ui/Badge";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import Icon from "../components/ui/Icon";
import Aviso from "../components/ui/Aviso";
import IconBadge from "../components/ui/IconBadge";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import OndaCabecalho from "../components/ui/OndaCabecalho";
import AnelProgresso from "../components/ui/AnelProgresso";
import { capitalizarPrimeira } from "../utils/texto";
import { capturarMelhorLocalizacao } from "../utils/geolocalizacao";
import { formatarHoras } from "../utils/tempo";

const CAMPO_FALTANDO_LABEL = {
  horario_entrada: "Horário de entrada",
  horario_saida: "Horário de saída",
  escala_referencia_data: "Data de referência da escala 12x36",
};

function linkBtnStyle() {
  return { background: "none", border: "none", color: COLORS.primary, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" };
}

const TIPO_LABEL = {
  entrada: "Entrada",
  saida: "Saída",
  intervalo_inicio: "Saída Intervalo",
  intervalo_fim: "Volta Intervalo",
};

const AFASTAMENTO_TIPO_LABEL = {
  ferias: "férias",
  atestado: "atestado médico",
  outro: "afastamento",
};

// Mesmo breakpoint do AppLayout (BREAKPOINT_MOBILE) — acima disso a tela
// troca o cabeçalho em onda (pensado pro formato estreito/alto do celular)
// por um layout "hero dividido" só de desktop: card de relógio+botão à
// esquerda, grade de informação à direita. Onda esticada numa tela larga
// ficava desproporcional e não aproveitava o espaço horizontal — pedido
// explícito do usuário depois de ver a versão web no navegador de PC.
const BREAKPOINT_DESKTOP = 860;

function distanciaMetros(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Diferença em minutos entre agora e um horário "HH:MM(:SS)" esperado — positivo
// é atraso, negativo é adiantado. Só informativo, nunca bloqueia a batida (quem
// decide de verdade se aceita é o backend).
function minutosDeDiferenca(agora, horarioEsperado) {
  if (!horarioEsperado) return null;
  const [h, m] = horarioEsperado.split(":").map(Number);
  const esperado = new Date(agora);
  esperado.setHours(h, m, 0, 0);
  return Math.round((agora - esperado) / 60000);
}

function saudacao(agora) {
  const h = agora.getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

// Cor do círculo de ação varia com o tipo de batida — mesma ideia do app
// mobile (ver mobile/src/app/(app)/index.js corDaBatida): verde é só pra
// "já bati a entrada, tudo em dia" (saída e o círculo de dia completo), não
// pra entrada em si — verde ali confundia, parecia "tudo certo" antes da
// pessoa ter batido qualquer coisa.
function corDaBatida(tipo) {
  if (tipo === "intervalo_inicio" || tipo === "intervalo_fim") return { cor: COLORS.warning, corFim: "#C2790A" };
  if (tipo === "saida") return { cor: COLORS.success, corFim: "#0F8A3C" };
  // Mesmo azul-marinho da sidebar (pedido do usuário), não mais o azul vivo
  // de antes — cor sólida, sem gradiente, igual o fundo da sidebar de verdade.
  return { cor: COLORS.sidebarBg, corFim: COLORS.sidebarBg };
}

// Ponto "seletor": a pessoa escolhe o que está batendo (entrada, saída de
// intervalo, volta do intervalo ou saída), quantas vezes precisar, sem ordem
// forçada. Só quem bate intervalo (cadastro) vê os dois cartões do meio.
const OPCOES_SELETOR = [
  { tipo: "entrada", numero: 1, cor: "#16A34A", fundo: "#DCFCE7" },
  { tipo: "intervalo_inicio", numero: 2, cor: "#2563EB", fundo: "#E3EBFB" },
  { tipo: "intervalo_fim", numero: 3, cor: "#D9A11B", fundo: "#FEF6DD" },
  { tipo: "saida", numero: 4, cor: "#F97316", fundo: "#FDEBE2" },
];

function SeletorTipoPonto({ bateIntervalo, registros, onEscolher }) {
  const opcoes = OPCOES_SELETOR
    .filter((o) => bateIntervalo || (o.tipo !== "intervalo_inicio" && o.tipo !== "intervalo_fim"))
    .map((o, i) => ({ ...o, numero: i + 1 }));
  return (
    <div>
      <p style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 12px" }}>Escolha o tipo do ponto</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {opcoes.map((o) => {
          const ultima = [...registros].reverse().find((r) => r.tipo === o.tipo);
          return (
            <button
              key={o.tipo}
              onClick={() => onEscolher(o.tipo)}
              style={{
                textAlign: "left", cursor: "pointer", background: o.fundo, border: `2.5px solid ${o.cor}`,
                borderRadius: 16, padding: "12px 14px", minHeight: 96, boxShadow: `5px 5px 0 0 ${o.cor}`,
              }}
            >
              <div style={{ fontSize: 26, fontWeight: 900, color: COLORS.text, lineHeight: 1 }}>{o.numero}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginTop: 6 }}>{TIPO_LABEL[o.tipo]}</div>
              {ultima && (
                <div style={{ fontSize: 11, color: COLORS.textMid, marginTop: 4 }}>
                  Última: {String(ultima.registrado_em).slice(11, 16)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Progresso do turno atual (fração 0-1 de quanto já passou desde a entrada
// até o horário de saída esperado) — pro anel visual. Mesma regra de virada
// de dia que o backend usa pra turno noturno (mesma lógica do app mobile).
function progressoJornada(status, agora) {
  if (!status?.horario_entrada || !status?.horario_saida) return null;
  const entradaRegistro = status.registros?.find((r) => r.tipo === "entrada");
  if (!entradaRegistro) return null;
  const entradaReal = new Date(String(entradaRegistro.registrado_em).replace(" ", "T"));
  const [hSaida, mSaida] = status.horario_saida.split(":").map(Number);
  const limite = new Date(entradaReal);
  limite.setHours(hSaida, mSaida, 0, 0);
  if (limite <= entradaReal) limite.setDate(limite.getDate() + 1);
  const total = limite - entradaReal;
  if (total <= 0) return null;
  const fracao = Math.min(Math.max((agora - entradaReal) / total, 0), 1);
  return { fracao, restanteMs: Math.max(limite - agora, 0) };
}

function formatarRestante(ms) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Botão/selo circular grande — o "coração" visual da tela: bater ponto vira
// um alvo redondo em vez de um botão retangular perdido no meio de texto.
// `interativo` diferencia o círculo clicável (bater ponto) dos círculos só
// de status (tudo certo / aguardando aprovação), que têm sombra mais suave
// e não reagem a hover.
// Virou um cartão grande (não mais um círculo pequeno) — ícone, título
// ("Bater Ponto" ou o status atual) e o relógio ficam todos dentro do próprio
// botão, pedido explícito do usuário inspirado no Pontotel (ver print
// compartilhado). `titulo`/`relogio` são opcionais pra não quebrar quem só
// precisa do ícone sozinho.
function CirculoAcao({ icon, cor, corFim, size = 132, interativo = true, titulo, relogio, ...props }) {
  const Tag = interativo ? "button" : "div";
  return (
    <Tag
      style={{
        // min() trava o crescimento em telas bem estreitas, sem perder o
        // tamanho grande pedido em telas normais — continua um quadrado
        // (mesma expressão nos dois eixos).
        width: `min(${size}px, 82vw)`, height: `min(${size}px, 82vw)`, borderRadius: Math.round(size * 0.22), border: "none", flexShrink: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: Math.round(size * 0.035),
        padding: "0 12px", boxSizing: "border-box",
        background: `linear-gradient(135deg, ${cor} 0%, ${corFim || cor} 100%)`,
        // Mesmo "contorno" sólido (sem blur, cor cheia) da sidebar — efeito
        // de cartão duplo/adesivo, não uma sombra normal (ver AppLayout.js).
        boxShadow: `12px 12px 0 0 ${COLORS.sidebarAccent}`,
        cursor: interativo ? "pointer" : "default",
        transition: "transform .15s, box-shadow .15s",
      }}
      onMouseEnter={(e) => { if (interativo) e.currentTarget.style.transform = "translateY(-2px) scale(1.02)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}
      {...props}
    >
      <Icon name={icon} size={Math.round(size * 0.2)} color="#fff" strokeWidth={2.2} />
      {titulo && (
        <span style={{ color: "#fff", fontWeight: 900, fontSize: Math.round(size * 0.1), textTransform: "uppercase", letterSpacing: "0.02em", textAlign: "center", lineHeight: 1.1 }}>
          {titulo}
        </span>
      )}
      {relogio && (
        <span style={{ color: "#fff", fontWeight: 800, fontSize: Math.round(size * 0.135), fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
          {relogio}
        </span>
      )}
    </Tag>
  );
}

// Botão quadrado com o mesmo contorno sólido deslocado (sem blur) da
// sidebar/CirculoAcao — aqui preenchido de cor (verde/vermelho), pra servir
// de "Cancelar"/"Bater mesmo assim" num alerta que não é mais o
// window.confirm nativo (pedido do usuário: manter a identidade visual do
// app também nos avisos).
function BotaoAcaoQuadrado({ cor, corSombra, texto, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 140, height: 84, borderRadius: 16, border: "none", cursor: "pointer",
        background: cor, color: "#fff", fontWeight: 800, fontSize: 14, textAlign: "center",
        boxShadow: `6px 6px 0 0 ${corSombra}`,
        transition: "transform .1s",
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = "translate(3px, 3px)"; e.currentTarget.style.boxShadow = `3px 3px 0 0 ${corSombra}`; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "translate(0, 0)"; e.currentTarget.style.boxShadow = `6px 6px 0 0 ${corSombra}`; }}
    >
      {texto}
    </button>
  );
}

// Versão sem número/técnica do status de localização — pensada pra quem tem
// dificuldade de leitura: só ícone + cor + uma frase curtíssima, sem metros
// nem precisão. A versão completa (com distância) continua só pro gestor,
// no cabeçalho de sempre.
function LocalizacaoSimples({ localizacao, onAtualizar }) {
  if (localizacao === null) {
    return <p style={{ fontSize: 13, color: COLORS.textLight, margin: "6px 0 0" }}>Verificando localização...</p>;
  }
  if (localizacao === "sem_gps") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <Badge tone="neutral">Localização indisponível</Badge>
        <button onClick={onAtualizar} style={linkBtnStyle()}>tentar de novo</button>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
      {localizacao.dentro ? (
        <Badge tone="success">Você está no lugar certo</Badge>
      ) : (
        <>
          <Badge tone="danger">Você não está no posto</Badge>
          <button onClick={onAtualizar} style={linkBtnStyle()}>atualizar</button>
        </>
      )}
    </div>
  );
}

export default function BaterPonto() {
  const { token, usuario } = useAuth();
  const [status, setStatus] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [naoVinculado, setNaoVinculado] = useState(false);
  // null = não se aplica; array = cadastro existe mas falta isso pra bater ponto.
  const [cadastroIncompleto, setCadastroIncompleto] = useState(null);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [enviando, setEnviando] = useState(false);

  const [agora, setAgora] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const [ehDesktop, setEhDesktop] = useState(window.innerWidth >= BREAKPOINT_DESKTOP);
  useEffect(() => {
    const fn = () => setEhDesktop(window.innerWidth >= BREAKPOINT_DESKTOP);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const [modalAberto, setModalAberto] = useState(false);
  const [stream, setStream] = useState(null);
  const [foto, setFoto] = useState(null); // { blob, url }
  // Só usado no modo "seletor": o tipo que a pessoa escolheu bater agora.
  const [tipoEscolhido, setTipoEscolhido] = useState(null);
  const videoRef = useRef(null);
  const cameraAtiva = !!stream;

  // Batida que falhou por falta de conexão (não um erro do servidor — o
  // fetch nem saiu) — guarda o formulário já montado (selfie e localização
  // já capturadas) pra reenviar sozinho quando a rede voltar, sem pedir pra
  // pessoa refazer a foto. { form, fotoUrl } | null.
  const [pendenteOffline, setPendenteOffline] = useState(null);
  const [tentandoReenviar, setTentandoReenviar] = useState(false);
  const [confirmandoForaDoPosto, setConfirmandoForaDoPosto] = useState(false);
  const resolverForaDoPostoRef = useRef(null);

  // "Já bateu esse tipo hoje, bater de novo mesmo assim?" — mesma ideia do
  // fora do posto logo acima, modal própria em vez de window.confirm
  // nativo, pra manter a identidade visual do app (pedido do usuário
  // 2026-10-09, no mobile — replicado aqui pra manter os dois iguais).
  const [confirmandoRepeticao, setConfirmandoRepeticao] = useState(false);
  const [tipoRepeticaoTexto, setTipoRepeticaoTexto] = useState("");
  const resolverRepeticaoRef = useRef(null);
  const confirmarRepeticao = (tipoLabel) =>
    new Promise((resolve) => {
      setTipoRepeticaoTexto(tipoLabel);
      resolverRepeticaoRef.current = resolve;
      setConfirmandoRepeticao(true);
    });
  const responderRepeticao = (valor) => {
    setConfirmandoRepeticao(false);
    resolverRepeticaoRef.current?.(valor);
    resolverRepeticaoRef.current = null;
  };

  const [horasBanco, setHorasBanco] = useState("");
  const [enviandoBanco, setEnviandoBanco] = useState(false);

  const [aceitandoTermos, setAceitandoTermos] = useState(false);

  // { distancia, dentro } | "sem_gps" (não deu pra obter localização) | null (carregando/não se aplica)
  const [localizacao, setLocalizacao] = useState(null);

  // Painel "Ver meu dia" (funcionário) — banco de horas, linha do tempo e
  // avisos só-informativos ficam escondidos aqui por padrão, longe do botão
  // principal. Perfil gestor não usa isso — vê tudo direto, como sempre.
  const [verMeuDia, setVerMeuDia] = useState(false);

  // `silencioso` evita o spinner de tela cheia piscando a cada atualização em
  // segundo plano — só a primeira carga (ou uma falha real nela) mostra
  // carregando/erro pra pessoa.
  const carregarStatus = useCallback(async (silencioso) => {
    if (!silencioso) {
      setCarregando(true);
      setErro("");
      setNaoVinculado(false);
      setCadastroIncompleto(null);
    }
    try {
      const data = await apiFetch("GET", "/api/ponto/hoje", undefined, token);
      if (data.cadastro_incompleto) {
        setCadastroIncompleto(data.campos_faltando || []);
        return;
      }
      setStatus(data);
      if (data.banco_hoje?.ja_declarado == null && data.banco_hoje?.diferenca > 0) {
        setHorasBanco(String(data.banco_hoje.diferenca));
      }
    } catch (e) {
      if (!silencioso) {
        if (e.status === 404) {
          setNaoVinculado(true);
        } else {
          setErro(e.message);
        }
      }
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [token]);

  useEffect(() => { carregarStatus(); }, [carregarStatus]);

  // Sem isso, uma mudança feita pelo gestor (ex: coordenada/raio do posto,
  // exige_foto, horário) só chegava na tela depois da pessoa dar F5 na mão —
  // achado com uso real (2026-08-31): o gestor atualizava o posto e a pessoa
  // continuava vendo a localização antiga até recarregar sozinha. Dois
  // gatilhos complementares: a aba voltar a ficar visível (o caso mais comum
  // — reabriu o app pra bater ponto) e um intervalo de segurança pra quem
  // deixa a aba aberta o tempo todo sem trocar de tela. Atualizando `status`
  // aqui também já refaz a checagem de localização sozinha, sem precisar de
  // nada a mais — ela depende de `status.postos` (ver useEffect abaixo).
  useEffect(() => {
    const aoFicarVisivel = () => {
      if (document.visibilityState === "visible") carregarStatus(true);
    };
    document.addEventListener("visibilitychange", aoFicarVisivel);
    // Só atualiza no intervalo se a aba estiver mesmo visível — sem essa
    // checagem, uma aba esquecida aberta em segundo plano continuava
    // batendo /api/ponto/hoje a cada 30s pra sempre, sem ninguém olhando.
    const id = setInterval(() => {
      if (document.visibilityState === "visible") carregarStatus(true);
    }, 30000);
    return () => {
      document.removeEventListener("visibilitychange", aoFicarVisivel);
      clearInterval(id);
    };
  }, [carregarStatus]);

  // Checa a localização assim que a tela carrega — antes de abrir a câmera —
  // pra avisar se a pessoa está fora da área de TODOS os postos vinculados
  // enquanto ainda dá tempo dela se aproximar, em vez de só descobrir isso
  // depois de tirar a selfie. Pode ter mais de um posto vinculado — testa
  // contra cada um com GPS configurado e fica com o melhor resultado (dentro
  // do raio tem prioridade sobre fora; entre dois "fora", o mais próximo).
  const postos = status?.postos || [];
  const precisaChecarLocalizacao = !!(
    postos.some((p) => p.exige_localizacao && p.latitude != null && p.longitude != null) && status?.proximo_tipo
  );
  // false = folga confirmada pela escala; null = não dá pra saber (12x36 sem
  // referência, ou sem escala) — só nesse primeiro caso exige justificativa.
  const folgaHoje = status?.esperado_hoje === false;
  // Configurável por funcionário (Funcionários > Exige foto?) — desligado,
  // vira bater ponto num toque só, sem passar pela câmera. Default true
  // (undefined/null) até o status carregar, pra nunca abrir o fluxo errado.
  const precisaFoto = status?.exige_foto !== false;
  const checarLocalizacao = useCallback(async () => {
    if (!precisaChecarLocalizacao) return;
    setLocalizacao(null);
    const local = await capturarMelhorLocalizacao();
    if (!local) {
      setLocalizacao("sem_gps");
      return;
    }
    const candidatos = (status?.postos || []).filter((p) => p.exige_localizacao && p.latitude != null && p.longitude != null);
    let melhor = null;
    for (const p of candidatos) {
      const distancia = Math.round(distanciaMetros(local.latitude, local.longitude, p.latitude, p.longitude));
      const dentro = distancia <= p.raio_metros;
      const ganha = !melhor || (dentro && !melhor.dentro) || (dentro === melhor.dentro && distancia < melhor.distancia);
      if (ganha) melhor = { nome: p.nome, distancia, dentro, raio_metros: p.raio_metros };
    }
    setLocalizacao({ ...melhor, accuracy: local.accuracy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precisaChecarLocalizacao, status?.postos]);

  useEffect(() => { checarLocalizacao(); }, [checarLocalizacao]);

  // Liga o stream no <video> assim que ele existe (o elemento só é montado quando
  // `stream` já não é nulo, então o ref sempre está pronto aqui) e garante que a
  // câmera é desligada de verdade — inclusive ao fechar a janela ou trocar de
  // página — antes que o navegador reclame de um play() pendente numa página
  // que já foi trocada.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
    return () => {
      video.pause();
      video.srcObject = null;
      stream.getTracks().forEach((t) => t.stop());
    };
  }, [stream]);

  const pararCamera = useCallback(() => setStream(null), []);

  const ligarCamera = async () => {
    setErro("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setStream(s);
    } catch (e) {
      setErro("Não foi possível acessar a câmera. Verifique a permissão no navegador.");
    }
  };

  // Avisa na hora (antes de abrir câmera/modal, que seria trabalho perdido)
  // quando o GPS já confirmou que a pessoa está fora do posto — o servidor
  // continua sendo quem decide de verdade (POST /api/ponto/bater), isso
  // aqui só evita descobrir só depois de já ter tirado a selfie. Sem
  // leitura ainda ou sem GPS no aparelho, deixa passar sem perguntar nada.
  // Modal própria (não window.confirm nativo) pra manter a identidade
  // visual do app também nos avisos — a resposta chega via Promise.
  const confirmarApesarDeForaDoPosto = () =>
    new Promise((resolve) => {
      resolverForaDoPostoRef.current = resolve;
      setConfirmandoForaDoPosto(true);
    });
  const responderForaDoPosto = (valor) => {
    setConfirmandoForaDoPosto(false);
    resolverForaDoPostoRef.current?.(valor);
    resolverForaDoPostoRef.current = null;
  };

  const handleBaterPrincipal = async (aoConfirmar) => {
    if (precisaChecarLocalizacao && localizacao && localizacao !== "sem_gps" && !localizacao.dentro) {
      const continuar = await confirmarApesarDeForaDoPosto();
      if (!continuar) return;
    }
    aoConfirmar();
  };

  // No modo seletor sempre abre perguntando o tipo primeiro (câmera só liga
  // depois que a pessoa escolhe, lá dentro do próprio modal — ver onEscolher
  // no Modal mais abaixo); no modo corrido, o tipo já é conhecido de cara.
  const abrirModal = () => {
    setFoto(null);
    setErro("");
    setTipoEscolhido(null);
    setModalAberto(true);
    if (status?.modo_ponto !== "seletor" && precisaFoto) ligarCamera();
  };

  const fecharModal = () => {
    setModalAberto(false);
    setFoto(null);
    setErro("");
    setTipoEscolhido(null);
    pararCamera();
  };

  const capturarFoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      setFoto({ blob, url: URL.createObjectURL(blob) });
      pararCamera();
    }, "image/jpeg", 0.9);
  };

  const refazerFoto = () => {
    if (foto) URL.revokeObjectURL(foto.url);
    setFoto(null);
    ligarCamera();
  };

  const mensagemSucesso = (resultado) =>
    `${TIPO_LABEL[resultado.tipo]} registrada às ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}.`;

  const confirmarBatida = async () => {
    const tipoAlvo = status?.modo_ponto === "seletor" ? tipoEscolhido : status?.proximo_tipo;
    if ((precisaFoto && !foto) || !tipoAlvo) return;
    setEnviando(true);
    setErro("");
    setSucesso("");
    // Declarado fora do try — precisa continuar acessível no catch (linha
    // abaixo, fila offline), e `const` dentro do try não sobrevive ao escopo
    // do bloco. Sem isso, exatamente o caminho de "sem conexão" (o motivo de
    // esse form existir) quebrava com "form is not defined" em vez de
    // guardar a batida pra reenviar depois.
    let form;
    try {
      const local = await capturarMelhorLocalizacao();
      form = new FormData();
      form.append("tipo", tipoAlvo);
      if (local) {
        form.append("latitude", local.latitude);
        form.append("longitude", local.longitude);
        if (local.accuracy) form.append("accuracy", local.accuracy);
      }
      if (foto) form.append("foto", foto.blob, "selfie.jpg");

      let resultado;
      try {
        resultado = await apiFetch("POST", "/api/ponto/bater", form, token);
      } catch (e) {
        if (e.status === 409 && e.data?.repeticao) {
          const confirmou = await confirmarRepeticao(TIPO_LABEL[tipoAlvo]);
          if (!confirmou) return;
          form.append("confirmarRepeticao", "true");
          resultado = await apiFetch("POST", "/api/ponto/bater", form, token);
        } else {
          throw e;
        }
      }
      if (foto) URL.revokeObjectURL(foto.url);
      setFoto(null);
      pararCamera();
      setModalAberto(false);
      setTipoEscolhido(null);
      setSucesso(mensagemSucesso(resultado));
      await carregarStatus();
    } catch (e) {
      if (e.status) {
        // servidor respondeu (validação, fora de sequência, etc) — erro de
        // verdade, não é caso de fila offline.
        setErro(e.message);
      } else {
        // fetch nem conseguiu sair pra rede — sem conexão. Guarda a foto (se
        // teve) e localização já capturadas, fecha o modal (não tem mais nada
        // pra pessoa fazer, virou processo automático em segundo plano) e
        // deixa o efeito de reenvio tentar de novo sozinho quando a internet
        // voltar, sem pedir pra refazer a selfie. `foto` some do estado de
        // propósito — assim a tela principal não oferece "bater ponto" de
        // novo enquanto já tem uma pendente (ver pendenteOffline abaixo).
        setPendenteOffline({ form, fotoUrl: foto?.url || null });
        setFoto(null);
        pararCamera();
        setModalAberto(false);
      }
    } finally {
      setEnviando(false);
    }
  };

  // Reenvia a batida que ficou pendente por falta de conexão — chamado
  // automaticamente (evento "online" do navegador + verificação periódica,
  // porque esse evento nem sempre dispara direito) e também pelo botão
  // "Tentar agora" do aviso.
  const tentarReenviarPendente = useCallback(async () => {
    if (!pendenteOffline) return;
    setTentandoReenviar(true);
    try {
      const resultado = await apiFetch("POST", "/api/ponto/bater", pendenteOffline.form, token);
      if (pendenteOffline.fotoUrl) URL.revokeObjectURL(pendenteOffline.fotoUrl);
      setPendenteOffline(null);
      setFoto(null);
      pararCamera();
      setModalAberto(false);
      setSucesso(mensagemSucesso(resultado));
      await carregarStatus();
    } catch (e) {
      if (e.status) {
        // agora que a rede voltou, o servidor respondeu com um erro de
        // verdade (ex: alguém já bateu essa mesma batida por outro meio
        // enquanto essa ficou pendente) — sai da fila e mostra o erro.
        setPendenteOffline(null);
        setErro(e.message);
      }
      // sem status: continua sem conexão, tenta de novo na próxima.
    } finally {
      setTentandoReenviar(false);
    }
  }, [pendenteOffline, token, carregarStatus, pararCamera]);

  useEffect(() => {
    if (!pendenteOffline) return;
    window.addEventListener("online", tentarReenviarPendente);
    const intervalo = setInterval(tentarReenviarPendente, 15000);
    return () => {
      window.removeEventListener("online", tentarReenviarPendente);
      clearInterval(intervalo);
    };
  }, [pendenteOffline, tentarReenviarPendente]);

  const declararBanco = async () => {
    const horas = Number(String(horasBanco).replace(',', '.'));
    if (!horas || horas <= 0) {
      setErro("Informe uma quantidade de horas válida.");
      return;
    }
    setEnviandoBanco(true);
    setErro("");
    setSucesso("");
    try {
      await apiFetch("POST", "/api/ponto/banco-horas", { horas }, token);
      setSucesso(`${horas}h do banco de horas usadas pra cobrir a diferença de hoje.`);
      await carregarStatus();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviandoBanco(false);
    }
  };

  const aceitarTermos = async () => {
    setAceitandoTermos(true);
    try {
      await apiFetch("POST", "/api/auth/aceitar-termos", undefined, token);
      await carregarStatus();
    } catch (e) {
      window.alert(e.message);
    } finally {
      setAceitandoTermos(false);
    }
  };

  if (carregando) {
    return <EstadoCarregando padding={0} />;
  }

  const souGestorOuAdmin = usuario?.perfil === "gestor" || usuario?.perfil === "admin_empresa";

  if (naoVinculado) {
    return (
      <Card>
        <p style={{ color: COLORS.danger, fontSize: 13.5, margin: 0 }}>
          {souGestorOuAdmin ? (
            <>
              Complete seu cadastro em{" "}
              <Link to="/funcionarios" style={{ color: COLORS.primary, fontWeight: 700 }}>Cadastro de Funcionário</Link>
              , e vincule com seu e-mail de gestor/administrador para começar a bater pontos.
            </>
          ) : (
            "Seu login ainda não está vinculado a um cadastro de funcionário. Peça ao seu gestor para vincular em Funcionários."
          )}
        </p>
      </Card>
    );
  }

  if (cadastroIncompleto) {
    return (
      <Card style={{ maxWidth: 480 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, color: COLORS.text, margin: "0 0 4px" }}>Falta completar seu cadastro</h2>
        <p style={{ fontSize: 13, color: COLORS.textMid, margin: "0 0 14px" }}>
          Pra registrar seu ponto, o sistema precisa saber sua jornada de trabalho. Ainda falta:
        </p>
        <ul style={{ margin: "0 0 18px", paddingLeft: 20, fontSize: 13.5, color: COLORS.text }}>
          {cadastroIncompleto.map((campo) => <li key={campo}>{CAMPO_FALTANDO_LABEL[campo] || campo}</li>)}
        </ul>
        {souGestorOuAdmin ? (
          <Link to="/funcionarios" style={{ color: COLORS.primary, fontWeight: 700, fontSize: 13.5 }}>Ir para Cadastro de Funcionário</Link>
        ) : (
          <p style={{ fontSize: 13, color: COLORS.textMid, margin: 0 }}>Peça ao seu gestor para completar em Cadastro de Funcionário.</p>
        )}
      </Card>
    );
  }

  // LGPD: bater ponto coleta selfie + localização a cada batida — precisa de
  // consentimento explícito antes da primeira vez, não é uma tela fechável.
  if (status && !status.termos_aceitos) {
    return (
      <Card style={{ maxWidth: 560 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, margin: "0 0 4px" }}>
          Antes de continuar
        </h2>
        <p style={{ fontSize: 13, color: COLORS.textMid, margin: "0 0 18px" }}>
          Pra registrar seu ponto, precisamos do seu consentimento.
        </p>
        <div style={{ fontSize: 13.5, color: COLORS.text, lineHeight: 1.7, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "14px 16px", marginBottom: 18 }}>
          Pra bater ponto, o PontoZipar coleta uma <strong>foto sua (selfie)</strong> e a{" "}
          <strong>sua localização</strong> no momento de cada batida. Isso serve como prova de que foi
          você, no horário certo e (quando aplicável) no local combinado — é a razão do sistema existir.
          <br /><br />
          Esses dados ficam guardados de forma segura e só o seu gestor tem acesso a eles. Você pode
          pedir pra ver ou excluir seus dados falando com seu gestor, respeitando o prazo mínimo que a
          lei exige pra manter registro de ponto. Ao aceitar, você concorda com essa coleta, conforme a
          Lei Geral de Proteção de Dados (LGPD).
        </div>
        <Btn onClick={aceitarTermos} disabled={aceitandoTermos} style={{ width: "100%" }}>
          {aceitandoTermos ? "Salvando..." : "Li e concordo"}
        </Btn>
      </Card>
    );
  }

  if (erro && !status) {
    return (
      <Card>
        <p style={{ color: COLORS.danger, fontSize: 13.5, margin: 0 }}>{erro}</p>
      </Card>
    );
  }

  const seletor = status?.modo_ponto === "seletor";
  // No modo seletor não existe "próximo" fixo: vale o que a pessoa escolheu.
  const proximo = seletor ? tipoEscolhido : status?.proximo_tipo;
  const bancoHoje = status?.banco_hoje;
  // O backend sempre retorna um próximo tipo agora (entrada ou saída, nunca
  // nulo — ver pontoCalculo.js) mesmo com turno noturno em aberto de ontem.
  // "Completo" passou a ser sinalizado por banco_hoje existir, não pela
  // ausência de próximo tipo.
  const diaCompleto = !seletor && !!bancoHoje;
  const podeCobrirBanco = bancoHoje && bancoHoje.diferenca > 0 && bancoHoje.ja_declarado == null;

  const horarioEsperado = proximo === "entrada" ? status?.horario_entrada : proximo === "saida" ? status?.horario_saida : null;
  const diffMin = minutosDeDiferenca(agora, horarioEsperado);

  // Perfil funcionário ganha a tela enxuta (pensada pra quem tem dificuldade
  // de leitura, testando principalmente pelo celular): só relógio, botão
  // gigante e o que exige ação de verdade. Gestor continua vendo tudo direto,
  // como sempre — não é o público que pediu simplificação.
  const souFuncionario = usuario?.perfil === "funcionario";

  // Só mostra o anel de progresso no meio do turno de verdade — já bateu
  // entrada, ainda não terminou o dia, sem justificativa/offline pendente.
  // Mesma regra do app mobile.
  const emTurno = !seletor && !pendenteOffline && !diaCompleto && proximo !== "entrada";
  const progresso = emTurno ? progressoJornada(status, agora) : null;
  const tamanhoCirculo = souFuncionario ? 340 : 280;
  const horaAgoraTexto = agora.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  // Cor de destaque do card hero (desktop) — acompanha o estado real da
  // batida (mesma lógica de corDaBatida/circuloElemento) em vez de ser
  // sempre azul, pra reforçar visualmente o que está acontecendo agora.
  const corHero = pendenteOffline
    ? COLORS.warning
    : diaCompleto
    ? COLORS.success
    : corDaBatida(proximo).cor;

  // O círculo flutua sobre a onda do cabeçalho (ver o return, mais abaixo) —
  // fica fora do card de status de propósito, é o elemento mais importante
  // da tela, não podia ficar "preso" dentro de uma caixa (mesma ideia do app).
  const circuloElemento = pendenteOffline ? (
    <CirculoAcao icon="clock" cor={COLORS.warning} corFim="#C2790A" interativo={false} size={tamanhoCirculo} titulo="Sincronizando..." relogio={horaAgoraTexto} />
  ) : diaCompleto ? (
    <CirculoAcao icon="checkCircle" cor={COLORS.success} corFim="#0F8A3C" interativo={false} size={tamanhoCirculo} titulo="Tudo certo" relogio={horaAgoraTexto} />
  ) : seletor ? (
    // Botão sempre genérico no modo seletor — o tipo só é perguntado depois
    // do clique, dentro do modal (pedido do usuário, inspirado no Pontotel).
    <CirculoAcao
      icon="clock"
      cor={COLORS.sidebarBg}
      corFim={COLORS.sidebarBg}
      onClick={() => handleBaterPrincipal(abrirModal)}
      disabled={enviando}
      size={tamanhoCirculo}
      titulo="Bater Ponto"
      relogio={horaAgoraTexto}
      aria-label="Bater ponto"
    />
  ) : (
    <CirculoAcao
      icon={proximo === "entrada" || proximo === "intervalo_fim" ? "logIn" : "logOut"}
      {...corDaBatida(proximo)}
      onClick={() => handleBaterPrincipal(!precisaFoto ? confirmarBatida : abrirModal)}
      disabled={enviando}
      size={tamanhoCirculo}
      titulo={`Bater ${TIPO_LABEL[proximo]}`}
      relogio={horaAgoraTexto}
      aria-label={`Bater ${TIPO_LABEL[proximo]}`}
    />
  );

  // Conteúdo de texto do card de status, abaixo do círculo flutuante — igual
  // pros dois perfis, só o texto de atraso/adiantado (numérico, "informação
  // difícil") some pro funcionário.
  const conteudoStatus = (
    <>
      {progresso && (
        <div style={{ marginBottom: 16 }}>
          <AnelProgresso progresso={progresso.fracao} size={souFuncionario ? 104 : 84} cor={COLORS.primary}>
            <div style={{ fontSize: 17, fontWeight: 900, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
              {formatarRestante(progresso.restanteMs)}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              faltam
            </div>
          </AnelProgresso>
        </div>
      )}

      {pendenteOffline ? (
        <>
          <p style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 6px" }}>Esperando conexão pra enviar</p>
          <p style={{ fontSize: 13, color: COLORS.textMid, margin: "0 auto", maxWidth: 280, textAlign: "left" }}>
            Sua batida já foi capturada. Assim que a internet voltar, ela é enviada sozinha.
          </p>
        </>
      ) : diaCompleto ? (
        <>
          <p style={{ fontSize: souFuncionario ? 20 : 15, fontWeight: 800, color: COLORS.text, margin: "0 0 2px" }}>Tudo certo por hoje</p>
          <p style={{ fontSize: 12.5, color: COLORS.textLight, margin: 0 }}>Todas as batidas de hoje já foram registradas.</p>
        </>
      ) : (
        <>
          {!seletor && !souFuncionario && diffMin !== null && diffMin > 5 && (
            <p style={{ fontSize: 12, color: COLORS.warning, margin: "0 0 12px", fontWeight: 600 }}>
              {diffMin} min depois do horário previsto ({horarioEsperado?.slice(0, 5)})
            </p>
          )}
          {!seletor && !souFuncionario && diffMin !== null && diffMin < -5 && (
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 12px" }}>
              {Math.abs(diffMin)} min antes do horário previsto ({horarioEsperado?.slice(0, 5)})
            </p>
          )}
        </>
      )}

      {erro && (
        <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px", marginTop: 16, textAlign: "left" }}>
          {erro}
        </div>
      )}
      {sucesso && (
        <div style={{ color: COLORS.success, fontSize: 13, background: COLORS.successBg, border: "1px solid #BBF7D0", borderRadius: 8, padding: "9px 13px", marginTop: 16, textAlign: "left" }}>
          {sucesso}
        </div>
      )}
    </>
  );

  // Conteúdo "extra" (banco de horas cobrindo diferença + badge de já
  // declarado) — só o gestor vê direto no card principal; pro funcionário
  // isso migra pra dentro de "Ver meu dia" (ver mais abaixo).
  const conteudoBancoDeHoras = (
    <>
      {diaCompleto && podeCobrirBanco && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 20, paddingTop: 18, textAlign: "left" }}>
          <p style={{ fontSize: 13, color: COLORS.textMid, margin: "0 0 10px" }}>
            Hoje faltou <strong>{formatarHoras(bancoHoje.diferenca)}</strong> pra completar sua jornada. Quer cobrir com o banco de horas?
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              type="number"
              step="0.25"
              min="0"
              value={horasBanco}
              onChange={(e) => setHorasBanco(e.target.value)}
              style={{ maxWidth: 100 }}
            />
            <Btn onClick={declararBanco} disabled={enviandoBanco} variant="secondary">
              {enviandoBanco ? "Enviando..." : "Cobrir com banco de horas"}
            </Btn>
          </div>
        </div>
      )}

      {diaCompleto && bancoHoje?.ja_declarado != null && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 20, paddingTop: 18 }}>
          <Badge tone="primary">{bancoHoje.ja_declarado}h do banco de horas usadas hoje</Badge>
        </div>
      )}
    </>
  );

  // Cor com propósito, não decoração — cada card ganha uma barrinha à
  // esquerda ligada ao que ele representa, sem preencher o fundo (pedido
  // explícito do usuário: gostou da borda, não do preenchimento/gradiente).
  const saldoBH = status?.saldo_banco_horas;
  const corSaldo = saldoBH > 0 ? COLORS.success : saldoBH < 0 ? COLORS.danger : COLORS.primary;
  const corTimeline = diaCompleto ? COLORS.success : status?.registros?.length > 0 ? COLORS.primary : COLORS.textLight;

  // Cards de saldo + linha do tempo de hoje — bloco reaproveitado nos dois
  // perfis, só muda de posição (sempre visível pro gestor, escondido atrás
  // de "Ver meu dia" pro funcionário).
  const cardsSaldoETimeline = (
    <div style={{ display: "flex", gap: 20, alignItems: "stretch", flexWrap: "wrap", marginTop: 20 }}>
      <Card style={{ flex: "1 1 200px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderLeft: `4px solid ${corSaldo}` }}>
        <div style={{ marginBottom: 12 }}>
          <IconBadge icon="clock" size={38} cor={corSaldo} />
        </div>
        <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
          Saldo banco de horas
        </p>
        <div
          style={{
            fontSize: 32,
            fontWeight: 900,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            color: saldoBH > 0 ? COLORS.success : saldoBH < 0 ? COLORS.danger : COLORS.text,
          }}
        >
          {formatarHoras(status?.saldo_banco_horas)}
        </div>
      </Card>

      <Card style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", justifyContent: "center", borderLeft: `4px solid ${corTimeline}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <IconBadge icon="calendar" size={32} cor={corTimeline} />
          <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>
            Hoje
          </p>
        </div>

        {/* Sempre mostra os passos do dia, preenchidos ou não — uma linha do
            tempo conta a história do dia melhor do que uma lista que
            simplesmente não existe antes da primeira batida. Só quem tem
            bate_intervalo ligado (ver Funcionários) vê os 2 passos do meio
            — pra quem não tem, o intervalo continua sendo um desconto
            automático, sem precisar bater nada. */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {(status?.bate_intervalo
            ? ["entrada", "intervalo_inicio", "intervalo_fim", "saida"]
            : ["entrada", "saida"]
          ).map((tipo, i, passos) => {
            const r = status?.registros?.find((x) => x.tipo === tipo);
            const feito = !!r;
            return (
              <div key={tipo} style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 26, flexShrink: 0 }}>
                  <div
                    style={{
                      width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: feito ? COLORS.success : COLORS.gray,
                    }}
                  >
                    <Icon name={tipo === "entrada" || tipo === "intervalo_fim" ? "logIn" : "logOut"} size={13} color={feito ? "#fff" : COLORS.textLight} />
                  </div>
                  {i < passos.length - 1 && (
                    <div style={{ width: 2, flex: 1, minHeight: 22, background: feito ? COLORS.success : COLORS.border, margin: "2px 0" }} />
                  )}
                </div>
                <div style={{ paddingBottom: 18 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: feito ? COLORS.text : COLORS.textLight }}>
                    {TIPO_LABEL[tipo]}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMid, display: "flex", alignItems: "center", gap: 5 }}>
                    {feito ? (
                      <>
                        <Link to={`/comprovante/${r.id}`} style={{ color: "inherit", textDecoration: "underline" }}>
                          {String(r.registrado_em).slice(11, 16)}
                        </Link>
                        {r.origem === "auto" && (
                          <span title="Fechamento automático — não foi uma batida sua, o sistema fechou sozinho 30min depois do seu horário de saída" style={{ display: "inline-flex", color: COLORS.warning }}>
                            <Icon name="alertCircle" size={12} />
                          </span>
                        )}
                      </>
                    ) : (
                      "Ainda não registrada"
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );

  // Acento do card de localização (desktop) — só tinge quando há uma
  // resposta de verdade (dentro/fora do posto); enquanto carrega ou não tem
  // GPS configurado o card fica neutro, sem sugerir um status que não existe.
  const localizacaoStatus = !precisaChecarLocalizacao || !localizacao || localizacao === "sem_gps"
    ? null
    : localizacao.dentro ? "ok" : "fora";
  const estiloCardLocalizacao =
    localizacaoStatus === "ok" ? { borderLeft: `4px solid ${COLORS.success}` } :
    localizacaoStatus === "fora" ? { borderLeft: `4px solid ${COLORS.danger}` } :
    {};

  // Postos/localização — mesmo conteúdo pros dois layouts, só muda onde é
  // encaixado (texto solto sob o círculo no layout de onda; dentro de um
  // Card na coluna direita do hero de desktop).
  const blocoPostosLocalizacao = souFuncionario ? (
    <>
      <p style={{ fontSize: 13, color: COLORS.textLight, margin: 0 }}>
        {postos.length > 0 ? postos.map((p) => p.nome).join(", ") : "Nenhum posto vinculado ao seu cadastro."}
      </p>
      {precisaChecarLocalizacao && <LocalizacaoSimples localizacao={localizacao} onAtualizar={checarLocalizacao} />}
    </>
  ) : (
    <>
      <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
        {postos.length > 0 ? `Posto${postos.length > 1 ? "s" : ""}: ${postos.map((p) => p.nome).join(", ")}` : "Nenhum posto vinculado ao seu cadastro."}
      </p>

      {precisaChecarLocalizacao && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
            {localizacao === null ? (
              <span style={{ fontSize: 12, color: COLORS.textLight }}>Verificando sua localização...</span>
            ) : localizacao === "sem_gps" ? (
              <>
                <Badge tone="neutral">Localização indisponível</Badge>
                <button onClick={checarLocalizacao} style={linkBtnStyle()}>tentar de novo</button>
              </>
            ) : localizacao.dentro ? (
              <Badge tone="success">Você está no posto {localizacao.nome} ({localizacao.distancia}m)</Badge>
            ) : (
              <>
                <Badge tone="danger">
                  {localizacao.distancia}m do posto {localizacao.nome} — máximo {localizacao.raio_metros}m
                </Badge>
                <button onClick={checarLocalizacao} style={linkBtnStyle()}>atualizar</button>
              </>
            )}
          </div>
          {localizacao && localizacao !== "sem_gps" && !localizacao.dentro && localizacao.accuracy > localizacao.raio_metros && (
            <p style={{ fontSize: 11, color: COLORS.textLight, margin: "4px 0 0" }}>
              Localização capturada com baixa precisão (margem de ~{Math.round(localizacao.accuracy)}m) — comum em computador sem GPS. Tente pelo celular, com o GPS ativado.
            </p>
          )}
        </div>
      )}
    </>
  );

  // Avisos do funcionário — divididos em "sempre visível" (pede ação de
  // verdade) e "dentro de Ver meu dia" (só informativo), igual já era.
  // Extraídos pra dar pra reaproveitar tanto no layout de onda quanto no
  // hero de desktop sem duplicar o JSX inteiro.
  const avisosSempreFuncionario = (
    <>
      {pendenteOffline && (
        <Aviso tone="warning" icon="alertCircle">
          Sem conexão — sua batida já foi capturada. Vamos enviar assim que a internet voltar.{" "}
          <button
            onClick={tentarReenviarPendente}
            disabled={tentandoReenviar}
            style={{ background: "none", border: "none", color: "inherit", fontWeight: 800, textDecoration: "underline", cursor: tentandoReenviar ? "default" : "pointer", padding: 0, fontSize: "inherit" }}
          >
            {tentandoReenviar ? "Tentando..." : "Tentar agora"}
          </button>
        </Aviso>
      )}

      {folgaHoje && !diaCompleto && (
        <Aviso tone="warning" icon="alertCircle">
          Hoje é sua folga.
        </Aviso>
      )}
    </>
  );

  const avisosExtraFuncionario = (
    <>
      {status?.feriado_hoje && (
        <Aviso tone="info" icon="calendar">
          Hoje é feriado ({status.feriado_hoje}). Não precisa bater ponto.
        </Aviso>
      )}
      {status?.afastamento_hoje && (
        <Aviso tone="info" icon="calendar">
          Você está de {AFASTAMENTO_TIPO_LABEL[status.afastamento_hoje.tipo] || "afastamento"} até{" "}
          {status.afastamento_hoje.data_fim.split("-").reverse().join("/")}.
        </Aviso>
      )}
      {status?.hora_extra_hoje?.status === "pendente" && (
        <Aviso tone="warning" icon="clock">
          Você trabalhou {status.hora_extra_hoje.horas}h além do previsto hoje. Aguardando aprovação do gestor.
        </Aviso>
      )}
    </>
  );

  const avisosGestor = (
    <>
      {pendenteOffline && (
        <Aviso tone="warning" icon="alertCircle">
          Sem conexão — sua batida já foi capturada, não precisa refazer nada. Vamos enviar assim que a internet voltar.{" "}
          <button
            onClick={tentarReenviarPendente}
            disabled={tentandoReenviar}
            style={{ background: "none", border: "none", color: "inherit", fontWeight: 800, textDecoration: "underline", cursor: tentandoReenviar ? "default" : "pointer", padding: 0, fontSize: "inherit" }}
          >
            {tentandoReenviar ? "Tentando..." : "Tentar agora"}
          </button>
        </Aviso>
      )}

      {status?.feriado_hoje && (
        <Aviso tone="info" icon="calendar">
          Hoje é feriado ({status.feriado_hoje}). Ninguém é cobrado de bater ponto, mas se você trabalhar normalmente é só bater igual sempre.
        </Aviso>
      )}

      {status?.afastamento_hoje && (
        <Aviso tone="info" icon="calendar">
          Você está de {AFASTAMENTO_TIPO_LABEL[status.afastamento_hoje.tipo] || "afastamento"} até{" "}
          {status.afastamento_hoje.data_fim.split("-").reverse().join("/")}. Não precisa bater ponto hoje, mas se vier trabalhar é só bater igual sempre.
        </Aviso>
      )}

      {folgaHoje && !diaCompleto && (
        <Aviso tone="warning" icon="alertCircle">
          Hoje é sua folga pela escala.
        </Aviso>
      )}

      {status?.hora_extra_hoje?.status === "pendente" && (
        <Aviso tone="warning" icon="clock">
          Você trabalhou {status.hora_extra_hoje.horas}h além do previsto hoje. Isso só entra no seu banco de horas depois que seu gestor aprovar.
        </Aviso>
      )}
    </>
  );

  const botaoVerMeuDia = (
    <button
      onClick={() => setVerMeuDia((v) => !v)}
      className="no-print"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
        background: "none", border: "none", color: COLORS.textMid, fontSize: 13, fontWeight: 700,
        cursor: "pointer", padding: "16px 0 4px",
      }}
    >
      {verMeuDia ? "Ocultar meu dia" : "Ver meu dia"}
      <Icon name="chevronDown" size={14} color={COLORS.textMid} strokeWidth={2.5} />
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 64px)" }}>
      {!ehDesktop && (
        <>
          {/* Cabeçalho em onda — mesma peça visual do app mobile (ver
              mobile/src/app/(app)/index.js). Estica pra largura do próprio
              container (a área de conteúdo já é isolada da sidebar/topo fixo
              pelo AppLayout), sem precisar de margem negativa igual no app. */}
          <OndaCabecalho height={200} className="onda-full-bleed">
            <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 30 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>
                {saudacao(agora)}, {usuario?.nome?.split(" ")[0] || "tudo bem"}
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 6 }}>
                {capitalizarPrimeira(agora.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" }))}
              </div>
              {precisaChecarLocalizacao && (
                <div style={{ marginTop: 8 }}>
                  {localizacao === null ? (
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>Verificando localização...</span>
                  ) : localizacao === "sem_gps" ? (
                    <Badge tone="neutral">Localização indisponível</Badge>
                  ) : localizacao.dentro ? (
                    <Badge tone="success">Você está no lugar certo</Badge>
                  ) : (
                    <Badge tone="danger">Você não está no posto</Badge>
                  )}
                </div>
              )}
            </div>
          </OndaCabecalho>

          {/* O círculo flutua metade na onda, metade no conteúdo — mesma conta
              de sempre (marginTop negativo em função do próprio tamanho). */}
          <div style={{ display: "flex", justifyContent: "center", position: "relative", zIndex: 2, marginTop: -(tamanhoCirculo / 2) - 10 }}>
            {circuloElemento}
          </div>

          <div style={{ textAlign: "center", marginTop: 10, marginBottom: 24 }}>
            {blocoPostosLocalizacao}
          </div>

          {souFuncionario ? (
            <div>
              {avisosSempreFuncionario}

              <Card style={{ textAlign: "center" }}>
                {conteudoStatus}
              </Card>

              {botaoVerMeuDia}

              {verMeuDia && (
                <div>
                  {avisosExtraFuncionario}

                  {(podeCobrirBanco || bancoHoje?.ja_declarado != null) && (
                    <Card style={{ marginBottom: 20 }}>{conteudoBancoDeHoras}</Card>
                  )}

                  {cardsSaldoETimeline}
                </div>
              )}
            </div>
          ) : (
            <div>
              {avisosGestor}

              <Card style={{ textAlign: "center" }}>
                {conteudoStatus}
                {conteudoBancoDeHoras}
              </Card>

              {cardsSaldoETimeline}
            </div>
          )}
        </>
      )}

      {ehDesktop && (
        // Hero dividido — pensado só pra desktop, sem a onda (que era
        // pensada pro formato estreito/alto do celular e ficava esticada
        // numa tela larga). Card à esquerda concentra relógio + botão de
        // bater (o "herói" da tela); coluna à direita junta postos/
        // localização, avisos e os cards de saldo/linha do tempo — usa a
        // largura sobrando em vez de empilhar tudo verticalmente.
        <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 24, alignItems: "start" }}>
          <Card style={{ textAlign: "center", padding: "32px 26px", borderTop: `4px solid ${corHero}` }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMid }}>
                {saudacao(agora)}, {usuario?.nome?.split(" ")[0] || "tudo bem"}
              </div>
              <div style={{ fontSize: 12.5, color: COLORS.textLight, marginTop: 6, marginBottom: 24 }}>
                {capitalizarPrimeira(agora.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" }))}
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>{circuloElemento}</div>

              <div style={{ marginTop: 22 }}>
                {conteudoStatus}
                {!souFuncionario && conteudoBancoDeHoras}
              </div>
            </div>
          </Card>

          <div>
            <Card style={{ textAlign: "center", marginBottom: 20, ...estiloCardLocalizacao }}>{blocoPostosLocalizacao}</Card>

            {souFuncionario ? (
              <>
                {avisosSempreFuncionario}
                {botaoVerMeuDia}
                {verMeuDia && (
                  <div>
                    {avisosExtraFuncionario}
                    {(podeCobrirBanco || bancoHoje?.ja_declarado != null) && (
                      <Card style={{ marginBottom: 20 }}>{conteudoBancoDeHoras}</Card>
                    )}
                    {cardsSaldoETimeline}
                  </div>
                )}
              </>
            ) : (
              <>
                {avisosGestor}
                {cardsSaldoETimeline}
              </>
            )}
          </div>
        </div>
      )}

      <Modal open={modalAberto} onClose={fecharModal} title={seletor && !tipoEscolhido ? "Bater ponto" : proximo ? `Bater ${TIPO_LABEL[proximo]}` : "Bater ponto"} width={400}>
        {seletor && !tipoEscolhido ? (
          <SeletorTipoPonto
            bateIntervalo={!!status?.bate_intervalo}
            registros={status?.registros || []}
            onEscolher={(tipo) => {
              setErro("");
              setTipoEscolhido(tipo);
              if (precisaFoto) ligarCamera();
            }}
          />
        ) : (
        <div style={{ textAlign: "center" }}>
          {seletor && (
            <button
              onClick={() => { setTipoEscolhido(null); pararCamera(); setFoto(null); setErro(""); }}
              style={{ background: "none", border: "none", color: COLORS.primary, fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 14, display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <Icon name="arrowLeft" size={13} /> Trocar tipo
            </button>
          )}
          {precisaFoto && (
            <div
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                margin: "0 auto 16px",
                borderRadius: RADIUS.lg,
                overflow: "hidden",
                background: COLORS.gray,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {foto ? (
                <img src={foto.url} alt="Selfie capturada" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : cameraAtiva ? (
                <video ref={videoRef} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <p style={{ fontSize: 13, color: COLORS.textMid, padding: 20 }}>Ligando câmera...</p>
              )}
            </div>
          )}

          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px", marginBottom: 14, textAlign: "left" }}>
              {erro}
            </div>
          )}

          {precisaFoto && !foto && cameraAtiva && <Btn onClick={capturarFoto} style={{ width: "100%" }}>Tirar foto</Btn>}
          {(!precisaFoto || foto) && (
            <div style={{ display: "flex", gap: 10 }}>
              {precisaFoto && <Btn variant="secondary" onClick={refazerFoto} disabled={enviando} style={{ flex: 1 }}>Refazer</Btn>}
              <Btn onClick={confirmarBatida} disabled={enviando} style={{ flex: 1 }}>
                {enviando ? "Enviando..." : "Confirmar"}
              </Btn>
            </div>
          )}
        </div>
        )}
      </Modal>

      <Modal open={confirmandoForaDoPosto} onClose={() => responderForaDoPosto(false)} title="Você não está no posto" width={400}>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: "0 0 20px" }}>
          Sua localização atual está fora do raio do posto de trabalho. Quer continuar mesmo assim?
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
          <BotaoAcaoQuadrado cor={COLORS.danger} corSombra="#B91C1C" texto="Cancelar" onClick={() => responderForaDoPosto(false)} />
          <BotaoAcaoQuadrado cor={COLORS.success} corSombra="#0F8A3C" texto="Bater mesmo assim" onClick={() => responderForaDoPosto(true)} />
        </div>
      </Modal>

      <Modal open={confirmandoRepeticao} onClose={() => responderRepeticao(false)} title="Bater de novo?" width={400}>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: "0 0 20px" }}>
          Você já bateu "{tipoRepeticaoTexto}" hoje. Quer bater de novo mesmo assim?
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
          <BotaoAcaoQuadrado cor={COLORS.danger} corSombra="#B91C1C" texto="Cancelar" onClick={() => responderRepeticao(false)} />
          <BotaoAcaoQuadrado cor={COLORS.success} corSombra="#0F8A3C" texto="Bater de novo" onClick={() => responderRepeticao(true)} />
        </div>
      </Modal>

    </div>
  );
}
